"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CanvasObject from "./CanvasObject";
import { ROOM_PHOTOS, roomPhotoLayout } from "./room-photos";

/**
 * What the piece is shown against.
 *
 * A sofa is the usual reference, but plenty of art goes above a bed, and some
 * people just want to see the picture without furniture in the way. Each room
 * is a photograph measured against real furniture, so the piece stays honestly
 * sized whichever is chosen.
 */
export type RoomScene = "sofa" | "bed" | "plain";

export interface RoomPreviewProps {
  src: string;
  alt: string;
  aspect: number;
  /** Trim size of the selected option, in millimetres. */
  widthMm: number;
  heightMm: number;
  /** What the scale reference is, per scene — the furniture differs, so the
   *  sentence naming its size has to differ with it. */
  captions: Record<RoomScene, string>;
  /** Copy for the scene switcher, keyed by scene. */
  sceneLabels: Record<RoomScene, string>;
  /** The part of `src` on the front of the canvas — see CanvasObject. */
  crop?: { x: number; y: number; width: number; height: number };
  /**
   * Which scenes to offer, in order; the first is shown first. Defaults to
   * all three. The canvas editor leaves out "plain" because its own crop view
   * already is the picture without furniture.
   */
  scenes?: RoomScene[];
  /**
   * A tighter cap on the room's height, in px — the map page's sticky band
   * on a phone, which has to leave the options visible under it. Never
   * raises the default cap.
   */
  maxHeight?: number;
}

function useElementWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

/**
 * How tall the room may be in a box `width` px wide: never above 520 px or
 * 95% of the width. `cap` (the map page's phone band) can only lower it.
 */
export function sceneHeightCap(width: number, cap?: number): number {
  return Math.min(cap ?? 520, 520, Math.round((width || 320) * 0.95));
}

/**
 * The piece on a wall, drawn to scale in a real room.
 *
 * "How big is 60 x 40 really?" is the question that stops people buying art
 * online, and a number in centimetres does not answer it. The room and the
 * artwork share one centimetre-to-pixel factor, so choosing a larger size
 * makes the piece visibly larger against a sofa or bed whose size the customer
 * already knows in their body. The furniture is a ruler first and decoration
 * second — see room-photos.ts for how each photograph is measured.
 */
export default function RoomPreview({
  src,
  alt,
  aspect,
  widthMm,
  heightMm,
  captions,
  sceneLabels,
  crop,
  scenes = ["sofa", "bed", "plain"],
  maxHeight: heightCap,
}: RoomPreviewProps) {
  const [scene, setScene] = useState<RoomScene>(scenes[0] ?? "sofa");
  const { ref, width } = useElementWidth();

  /**
   * How tall the scene is allowed to be.
   *
   * A full-width mobile column would otherwise produce a room nearly as tall
   * as it is wide, pushing the size picker off the screen. Cap it, and let the
   * room scale down to fit rather than overflow.
   *
   * The cap used to be 72% of the width, which on a phone is the binding
   * constraint — the room was letterboxed, and since the scale is the smaller
   * of the two fits, closing the window in on the piece bought nothing at all.
   * A portrait screen has the height to spare, so the room may now be nearly
   * square; the size picker still clears the fold because the room is wider
   * than it is tall in every case that matters.
   */
  const maxHeight = sceneHeightCap(width, heightCap);

  const photo = scene === "plain" ? null : ROOM_PHOTOS[scene];
  // The window is framed for the piece on the wall — a 40 x 60 is shot closer
  // than a 60 x 90, instead of every size being framed for the largest.
  const layout = React.useMemo(
    () =>
      photo
        ? roomPhotoLayout(photo, width, maxHeight, {
            widthCm: widthMm / 10,
            heightCm: heightMm / 10,
          })
        : null,
    [photo, width, maxHeight, widthMm, heightMm],
  );

  /**
   * "No furniture" has no ruler, so holding it to a room's scale made a 60 cm
   * picture occupy a fifth of the frame — the customer asked to see the
   * picture and got a large empty wall. Here the piece is simply shown as
   * large as it will go.
   */
  const PLAIN_FILL = 0.82;
  const plainPxPerCm =
    width > 0 && widthMm > 0 && heightMm > 0
      ? Math.min(
          (width * PLAIN_FILL) / (widthMm / 10),
          (maxHeight * PLAIN_FILL) / (heightMm / 10),
        )
      : 0;

  const pxPerCm = layout ? layout.pxPerCm : plainPxPerCm;
  const sceneWidth = layout ? layout.sceneWidth : width;
  const sceneHeight = layout ? layout.sceneHeight : maxHeight;

  const artWidthPx = (widthMm / 10) * pxPerCm;
  const artHeightPx = (heightMm / 10) * pxPerCm;
  // Hung at a real height above the photograph's floor; with no furniture
  // there is no floor to measure from, so the piece is centred.
  const artTop =
    layout && photo
      ? layout.floorY - photo.hangCentreCm * pxPerCm - artHeightPx / 2
      : (sceneHeight - artHeightPx) / 2;

  return (
    <Box>
      {/*
        The measuring element is full width; the ROOM inside it is only as wide
        as the room actually is. They differ whenever height is the binding
        constraint — a wide, short box — and the room then sits centred and
        narrower than its container, rather than showing grey bands beside the
        photograph.
      */}
      <Box ref={ref} sx={{ width: "100%" }}>
        <Box
          sx={{
            position: "relative",
            width: sceneWidth ? `${sceneWidth}px` : "100%",
            mx: "auto",
            height: pxPerCm ? sceneHeight : 320,
            bgcolor: "background.default",
            backgroundImage:
              "radial-gradient(75% 55% at 50% 0%, rgba(255,255,255,.55) 0%, rgba(255,255,255,0) 65%)," +
              "linear-gradient(180deg, rgba(17,29,41,.04) 0%, rgba(17,29,41,.10) 100%)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {layout && photo && (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              src={photo.src}
              alt=""
              aria-hidden
              style={{
                position: "absolute",
                left: layout.img.left,
                top: layout.img.top,
                width: layout.img.width,
                height: layout.img.height,
                maxWidth: "none",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          )}

          {pxPerCm > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                top: artTop,
              }}
            >
              <CanvasObject
                src={src}
                alt={alt}
                aspect={aspect}
                width={artWidthPx}
                depth={Math.max(4, Math.round(artWidthPx * 0.02))}
                crop={crop}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        {scenes.length > 1 && (
          <ToggleButtonGroup
            exclusive
            value={scene}
            onChange={(_, next) => next && setScene(next as RoomScene)}
          >
            {scenes.map((value) => (
              <ToggleButton
                key={value}
                value={value}
                sx={{ px: 2.5, py: 0.75, fontSize: 15, fontWeight: 700, textTransform: "none" }}
              >
                {sceneLabels[value]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        <Typography variant="caption" color="text.secondary">
          {captions[scene]}
        </Typography>
      </Box>
    </Box>
  );
}
